import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { get, post } from "../../components/Utils/CallApi";

export const fetchFeatures = createAsyncThunk(
  "features/fetchFeatures",
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const state = getState();
      const { isImpersonating, companyId } = state.app;

      const params = isImpersonating
        ? "impersonate=true&companyId=" + companyId
        : "";
      const response = await get("features/list", params);

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result || "Unknown error occurred");
      }

      const features = response.result?.map((feature) => ({
        ...feature,
        isActive: feature.is_enabled === 1,
      }));

      return features;
    } catch (error) {
      dispatch(setError(error.message || "Failed to fetch features"));
      return rejectWithValue(error.message || "Failed to fetch features");
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const saveGallery = createAsyncThunk(
  "gallery/saveGallery",
  async (
    { fk_sectionDataId, files },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      dispatch(setPageLoadingTrue());
      const state = getState();

      // Include fk_sectionDataId in postBody
      const postBody = {
        fk_sectionDataId,
      };

      const response = await post(
        "sections/data/gallery/save",
        postBody, // Include postBody with section ID
        state.app,
        null,
        files
      );

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result);
      }

      return response.data;
    } catch (error) {
      dispatch(setError("An error has occurred"));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const getConfig = createAsyncThunk(
  "config/getConfig",
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const state = getState();
      const { isImpersonating, companyId } = state.app;

      const params = "companyId=" + companyId;
      const response = await get("config/get", params);

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result || "Unknown error occurred");
      }
      return response.result;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch features");
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const getGallery = createAsyncThunk(
  "gallery/getGallery",
  async (fk_sectionDataId, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const state = getState();

      const params = "fk_sectionDataId=" + fk_sectionDataId;
      const response = await get("sections/data/gallery/get", params);

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result || "Unknown error occurred");
      }
      return response.result;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch features");
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const deleteAttachment = createAsyncThunk(
  "gallery/deleteAttachment",
  async (id, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const state = getState();

      // Include fk_sectionDataId in postBody
      const postBody = {
        id,
      };

      const response = await post(
        "sections/data/gallery/delete",
        postBody,
        state.app,
        null,
        null
      );

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result || "Unknown error occurred");
      }
      return response.result;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch features");
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const enableFeature = createAsyncThunk(
  "features/enable",
  async ({ featureId, enabled }, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const state = getState();
      const { companyId } = state.app;

      if (!companyId) {
        throw new Error("Company ID is required to enable/disable a feature.");
      }

      const body = {
        featureId,
        companyId,
        enabled,
      };

      const response = await post("features/enable", body);

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result || "Failed to toggle feature");
      }

      return response.result;
    } catch (error) {
      return rejectWithValue(
        error.message || "An error occurred while toggling the feature"
      );
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);
export const changePassword = createAsyncThunk(
  "user/changePassword",
  async (
    { username, current_password, new_password },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      dispatch(setPageLoadingTrue());
      const state = getState();
      const { companyId } = state.app;

      const body = {
        username,
        current_password,
        new_password,
      };

      const response = await post("user/changePassword", body);

      if (response.status === "failure") {
        return rejectWithValue(response.result);
      }

      return response.result;
    } catch (error) {
      return rejectWithValue(
        error.message || "An error occurred while changing password"
      );
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

const appSlice = createSlice({
  name: "app",
  initialState: {
    error: null,
    pageLoading: false,
    isImpersonating: false,
    companyId: localStorage.getItem("companyId"),
    features: null,
    config: null,
    gallery: null,
  },
  reducers: {
    setPageLoadingTrue: (state) => {
      state.pageLoading = true;
    },
    setPageLoadingFalse: (state) => {
      state.pageLoading = false;
    },
    startImpersonation: (state, action) => {
      state.isImpersonating = true;
      state.companyId = action.payload;
    },
    stopImpersonation: (state) => {
      state.isImpersonating = false;
      state.companyId = null;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setCompanyId: (state, action) => {
      state.companyId = action.payload;
    },
    setFeatures: (state, action) => {
      state.features = action.payload;
    },
    clearConfig: (state) => {
      state.config = null;
    },
    resetAppState: (state) => {
      state.isImpersonating = false;
      state.companyId = null;
      state.features = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeatures.fulfilled, (state, action) => {
        state.features = action.payload;
      })
      .addCase(fetchFeatures.rejected, (state, action) => {
        console.error("Failed to fetch features:", action.payload);
      })
      .addCase(getConfig.fulfilled, (state, action) => {
        state.config = action.payload;
      })
      .addCase(getConfig.rejected, (state, action) => {
        console.error("Failed to get config:", action.payload);
      })
      .addCase(saveGallery.fulfilled, (state, action) => {
        state.gallery = action.payload || []; // ✅ Correctly update gallery state
      })
      .addCase(deleteAttachment.fulfilled, (state, action) => {
        const deletedId = action.meta.arg; // ✅ Extract the deleted ID
        if (state.gallery) {
          state.gallery = state.gallery.filter((item) => item.id !== deletedId);
        }
      })
      .addCase(getGallery.fulfilled, (state, action) => {
        state.gallery = action.payload || []; // ✅ Update state after fetching gallery
      });
  },
});

export const {
  setPageLoadingTrue,
  setPageLoadingFalse,
  startImpersonation,
  stopImpersonation,
  setFeatures,
  setCompanyId,
  setError,
  clearError,
  resetAppState,
  clearConfig,
} = appSlice.actions;

export default appSlice.reducer;
